export const initialLayers = {
  ranking: true,
  industry: true,
  recommend: true,
  anchorOnly: false,
};

export const formatNumber = new Intl.NumberFormat("en-US");
const DEFAULT_WEIGHTS = { lq: 0.32, gap: 0.24, scale: 0.18, momentum: 0.18, growth: 0.08 };

const REGION_STATES = {
  "new england": ["Connecticut", "Maine", "Massachusetts", "New Hampshire", "Rhode Island", "Vermont"],
  "mid-atlantic": ["New Jersey", "New York", "Pennsylvania", "Delaware", "Maryland"],
  northeast: ["Connecticut", "Maine", "Massachusetts", "New Hampshire", "Rhode Island", "Vermont", "New Jersey", "New York", "Pennsylvania"],
  southeast: ["Alabama", "Arkansas", "Florida", "Georgia", "Kentucky", "Louisiana", "Mississippi", "North Carolina", "South Carolina", "Tennessee", "Virginia", "West Virginia"],
  south: ["Alabama", "Arkansas", "Florida", "Georgia", "Kentucky", "Louisiana", "Mississippi", "North Carolina", "Oklahoma", "South Carolina", "Tennessee", "Texas", "Virginia", "West Virginia"],
  southern: ["Alabama", "Arkansas", "Florida", "Georgia", "Kentucky", "Louisiana", "Mississippi", "North Carolina", "Oklahoma", "South Carolina", "Tennessee", "Texas", "Virginia", "West Virginia"],
  midwest: ["Illinois", "Indiana", "Iowa", "Kansas", "Michigan", "Minnesota", "Missouri", "Nebraska", "North Dakota", "Ohio", "South Dakota", "Wisconsin"],
  "great lakes": ["Illinois", "Indiana", "Michigan", "Minnesota", "New York", "Ohio", "Pennsylvania", "Wisconsin"],
  "rust belt": ["Illinois", "Indiana", "Michigan", "Ohio", "Pennsylvania", "West Virginia", "Wisconsin", "New York"],
  plains: ["Iowa", "Kansas", "Missouri", "Nebraska", "North Dakota", "Oklahoma", "South Dakota"],
  southwest: ["Arizona", "New Mexico", "Oklahoma", "Texas", "Nevada"],
  "mountain west": ["Colorado", "Idaho", "Montana", "Nevada", "Utah", "Wyoming"],
  west: ["Alaska", "Arizona", "California", "Colorado", "Hawaii", "Idaho", "Montana", "Nevada", "New Mexico", "Oregon", "Utah", "Washington", "Wyoming"],
  "west coast": ["California", "Oregon", "Washington"],
  "pacific northwest": ["Idaho", "Oregon", "Washington"],
  "sun belt": ["Alabama", "Arizona", "California", "Florida", "Georgia", "Louisiana", "Mississippi", "Nevada", "New Mexico", "North Carolina", "South Carolina", "Texas"],
  appalachia: ["Alabama", "Georgia", "Kentucky", "Maryland", "Mississippi", "New York", "North Carolina", "Ohio", "Pennsylvania", "South Carolina", "Tennessee", "Virginia", "West Virginia"],
  "gulf coast": ["Alabama", "Florida", "Louisiana", "Mississippi", "Texas"],
};

const INTENT_RULES = [
  { re: /\b(growing|growth|fast[- ]growing|momentum|expanding|booming|hot market)\b/, weights: { growth: 0.2, momentum: 0.26 }, emphasis: "growth momentum" },
  { re: /\b(cluster|concentration|concentrated|hub|ecosystem|deep bench|talent pool|supply chain|supplier base)\b/, weights: { lq: 0.46 }, emphasis: "existing cluster" },
  { re: /\b(large|big|scale|sizeable|major|workforce|employment base|labor pool)\b/, weights: { scale: 0.3 }, emphasis: "employment scale" },
  { re: /\b(emerging|undiscovered|underserved|overlooked|hidden|untapped|under[- ]the[- ]radar|early)\b/, weights: { gap: 0.36, momentum: 0.1 }, emphasis: "emerging / overlooked" },
  { re: /\b(don'?t already dominate|not saturated|less competitive|avoid saturated|not dominated|no incumbent|less crowded|whitespace|no ?(?:one|body) (?:dominat|owns|has)|without a dominant|nobody dominat)/, weights: { gap: 0.38, lq: 0.24 }, emphasis: "low saturation" },
  { re: /\b(established|proven|mature|strong performer|top ranked|best)\b/, weights: { momentum: 0.32, gap: 0.1 }, emphasis: "proven performers" },
  { re: /\b(low cost|cheap|affordable|cost[- ]sensitive|inexpensive)\b/, weights: { gap: 0.3 }, emphasis: "lower-cost geographies" },
];

export function padId(id, len) {
  return String(id).padStart(len, "0");
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function logScale(value, max) {
  return Math.log1p(value || 0) / Math.log1p(max || 1);
}

function parseNumber(match) {
  if (!match) return null;
  let value = Number.parseFloat(match[1].replace(/,/g, ""));
  const unit = (match[2] || "").toLowerCase();
  if (unit.startsWith("m")) value *= 1000000;
  else if (unit.startsWith("k")) value *= 1000;
  return value;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function rankColor(rank, maxRank) {
  if (!rank) return "#edf2f7";
  const ratio = rank / maxRank;
  if (ratio <= 0.1) return "#08306b";
  if (ratio <= 0.25) return "#2171b5";
  if (ratio <= 0.45) return "#6baed6";
  if (ratio <= 0.65) return "#c6dbef";
  if (ratio <= 0.85) return "#fdd49e";
  return "#ef6548";
}

const modeConfig = {
  state: {
    label: "State",
    plural: "States",
    maxRank: 50,
    geoKey: "states",
    industryKey: "industry_states",
    indexKey: "states",
    industryIndexKey: "industryStates",
    idField: "state_fips",
    topListSize: 20,
    topHighlight: 15,
  },
  metro: {
    label: "Metro",
    plural: "Metros",
    maxRank: 379,
    geoKey: "metros",
    industryKey: "industry_metros",
    indexKey: "metros",
    industryIndexKey: "industryMetros",
    idField: "cbsa",
    topListSize: 35,
    topHighlight: 40,
  },
  county: {
    label: "County",
    plural: "Counties",
    maxRank: 3144,
    geoKey: "counties",
    industryKey: "industry_counties",
    indexKey: "counties",
    industryIndexKey: "industryCounties",
    idField: "fips",
    topListSize: 35,
    topHighlight: 100,
  },
};

export function getModeConfig(mode) {
  return modeConfig[mode] || modeConfig.state;
}

export function recommendationColor(score) {
  if (score >= 75) return "#0f766e";
  if (score >= 60) return "#2563eb";
  if (score >= 45) return "#f97316";
  return "#94a3b8";
}

export function getGeoId(mode, row) {
  return row?.[getModeConfig(mode).idField];
}

export function labelGeo(row) {
  return row?.short || row?.name || "";
}

export function buildIndexes(data) {
  const states = new Map();
  const metros = new Map();
  const counties = new Map();
  const industryStates = new Map();
  const industryMetros = new Map();
  const industryCounties = new Map();

  (data.states || []).forEach((row) => states.set(row.state_fips, row));
  (data.metros || []).forEach((row) => metros.set(row.cbsa, row));
  (data.counties || []).forEach((row) => counties.set(row.fips, row));
  (data.industry_states || []).forEach((row) => {
    industryStates.set(`${row.naics}|${row.state_fips}`, row);
  });
  (data.industry_metros || []).forEach((row) => {
    industryMetros.set(`${row.naics}|${row.cbsa}`, row);
  });
  (data.industry_counties || []).forEach((row) => {
    industryCounties.set(`${row.naics}|${row.fips}`, row);
  });

  return { states, metros, counties, industryStates, industryMetros, industryCounties };
}

export function optionText(mode, row) {
  if (mode === "state") {
    return `#${row.rank} ${row.name} (${row.abbr})`;
  }
  if (mode === "metro") {
    return `#${row.rank} ${row.name}`;
  }
  return `#${row.rank} ${row.name}`;
}

function buildReason(row, industry, rankGap, referenceRank) {
  const parts = [];
  if (referenceRank) {
    parts.push(
      rankGap > 0
        ? `Ranked ${formatNumber.format(rankGap)} Places Below The Strongest Anchor`
        : "Above Or Near Anchor",
    );
  }
  if (industry.lq >= 1.25) parts.push(`Strong Cluster LQ ${industry.lq}`);
  else if (industry.lq >= 1) parts.push(`Above-Average LQ ${industry.lq}`);
  else parts.push(`Industry Presence LQ ${industry.lq}`);
  parts.push(`${formatNumber.format(industry.emp)} Employees`);
  if (row.avg_growth_pct) parts.push(`Growth Percentile ${Math.round(row.avg_growth_pct)}`);
  if (row.investment_signal) parts.push(`Investment Signal ${Math.round(row.investment_signal)}`);
  return parts.join(" | ");
}

function resolveWeights(criteria) {
  const weights = { ...DEFAULT_WEIGHTS, ...(criteria?.weights || {}) };
  const total = Object.values(weights).reduce((sum, value) => sum + value, 0) || 1;
  return Object.fromEntries(Object.entries(weights).map(([key, value]) => [key, value / total]));
}

function describeCriteria(criteria) {
  const parts = [];
  if (criteria.emphasis) parts.push(`weighting toward ${criteria.emphasis}`);
  if (criteria.stateNames.length) {
    parts.push(`limited to ${criteria.stateNames.length} state${criteria.stateNames.length === 1 ? "" : "s"}`);
  }
  if (criteria.maxPopulation) parts.push(`population under ${formatNumber.format(criteria.maxPopulation)}`);
  if (criteria.minPopulation) parts.push(`population over ${formatNumber.format(criteria.minPopulation)}`);
  if (criteria.minLq) parts.push(`LQ at least ${criteria.minLq}`);
  return parts.length ? `Read as: ${parts.join("; ")}.` : "";
}

const INDUSTRY_STOP_WORDS = new Set([
  "and", "or", "of", "the", "other", "all", "except", "related", "product", "products",
  "services", "manufacturing", "industry", "industries",
]);

function industryWords(value) {
  return (String(value || "").toLowerCase().match(/[a-z]+/g) || [])
    .filter((word) => word.length > 2 && !INDUSTRY_STOP_WORDS.has(word));
}

export function matchReferenceIndustry(text, industries) {
  const query = String(text || "").toLowerCase();
  const codeMatch = query.match(/\bnaics\s*(?:code\s*)?[:#-]?\s*(\d{2,6})\b/);
  const codes = codeMatch ? [codeMatch[1]] : (query.match(/\b\d{4,6}\b/g) || []);
  for (const code of codes) {
    const match = industries.find((industry) =>
      industry.naics === code || (industry.search_codes || []).includes(code));
    if (match) return match.naics;
  }
  if (codeMatch) return null; // Never replace an explicit unsupported code with a guess.

  const words = new Set(industryWords(query));
  let best = null;
  let bestScore = 0;
  let tied = false;
  for (const industry of industries) {
    let score = 0;
    for (const title of [industry.label, ...(industry.search_titles || [])]) {
      const normalized = String(title || "").toLowerCase();
      const titleWords = new Set(industryWords(title));
      const hits = [...titleWords].filter((word) => [...words].some((input) =>
        word === input || (input.length >= 4 && word.startsWith(input)))).length;
      const candidate = normalized && query.includes(normalized) ? 100 + normalized.length : hits;
      score = Math.max(score, candidate);
    }
    if (score > bestScore) {
      best = industry.naics;
      bestScore = score;
      tied = false;
    } else if (score > 0 && score === bestScore) {
      tied = true;
    }
  }
  return tied ? null : best;
}

export function interpretTargetingPrompt(text, { industries = [], stateNames = [] } = {}) {
  const criteria = {
    level: null,
    naics: null,
    anchorNames: [],
    stateNames: [],
    weights: null,
    minLq: null,
    minPopulation: null,
    maxPopulation: null,
    emphasis: null,
    rationale: "",
  };
  const query = ` ${(text || "").toLowerCase()} `;
  if (!query.trim()) return criteria;

  if (/\b(count(y|ies))\b/.test(query)) criteria.level = "county";
  else if (/\b(metro|metros|msa|cbsa|metropolitan)\b/.test(query)) criteria.level = "metro";
  else if (/\b(state|states|statewide)\b/.test(query)) criteria.level = "state";

  criteria.naics = matchReferenceIndustry(query, industries);

  const states = new Set();
  let residual = query;
  stateNames.forEach((name) => {
    const re = new RegExp(`\\b${escapeRegExp(name.toLowerCase())}\\b`, "g");
    if (re.test(residual)) {
      states.add(name);
      residual = residual.replace(re, " ");
    }
  });
  Object.keys(REGION_STATES)
    .sort((a, b) => b.length - a.length)
    .forEach((key) => {
      const re = new RegExp(`\\b${key.replace(/[-]/g, "[- ]")}\\b`);
      if (!re.test(residual)) return;
      REGION_STATES[key].forEach((state) => states.add(state));
      residual = residual.replace(re, " ");
    });
  criteria.stateNames = [...states];

  const weights = {};
  const emphasis = [];
  INTENT_RULES.forEach((rule) => {
    if (!rule.re.test(query)) return;
    Object.assign(weights, rule.weights);
    emphasis.push(rule.emphasis);
  });
  criteria.weights = Object.keys(weights).length ? weights : null;
  criteria.emphasis = emphasis.length ? emphasis.join(" + ") : null;

  if (/\b(rural|small town|non[- ]metro|smaller communit)\b/.test(query)) criteria.maxPopulation = 250000;
  if (/\b(major metro|large metro|big city|large market)\b/.test(query)) criteria.minPopulation = 1000000;

  const lq = query.match(/lq (?:of |at least |above |over )?([\d.]+)/);
  if (lq) criteria.minLq = Number.parseFloat(lq[1]);

  const under = query.match(/(?:under|below|less than|fewer than) ([\d,.]+)\s*(million|m|thousand|k)?\b.{0,24}(?:people|population|residents)/);
  if (under) criteria.maxPopulation = parseNumber(under);
  const over = query.match(/(?:over|above|more than|at least) ([\d,.]+)\s*(million|m|thousand|k)?\b.{0,24}(?:people|population|residents)/);
  if (over) criteria.minPopulation = parseNumber(over);

  const anchorRe = new RegExp(
    "\\b(?:like|similar to|compared to|versus|vs\\.?|against|anchored on|" +
      "(?:we'?re|we are|our (?:plant|facility|operation)s? (?:is|are))" +
      "(?:\\s+(?:already|currently|now))?\\s+(?:in|based in|headquartered in)|" +
      "based in|headquartered in|expanding from)\\s+([A-Za-z][A-Za-z.\\- ]{2,40})",
    "gi",
  );
  let match = anchorRe.exec(text || "");
  while (match) {
    const name = match[1]
      .replace(/\s+(and|but|where|that|which|with|so|because)\b.*$/i, "")
      .replace(/[.,;:]+$/, "")
      .trim();
    if (name.length > 2) criteria.anchorNames.push(name);
    match = anchorRe.exec(text || "");
  }

  criteria.rationale = describeCriteria(criteria);
  return criteria;
}

export function resolveCriteria(criteria, rows, mode) {
  const byName = new Map();
  rows.forEach((row) => {
    const id = getGeoId(mode, row);
    if (!id) return;
    [row.name, row.short, row.abbr, row.county]
      .filter(Boolean)
      .forEach((value) => byName.set(String(value).toLowerCase(), id));
    const lead = String(row.name || "")
      .toLowerCase()
      .split(",")[0]
      .split("-")[0]
      .trim();
    if (lead && !byName.has(lead)) byName.set(lead, id);
  });

  const anchorIds = [];
  const unmatchedAnchors = [];
  (criteria.anchorNames || []).forEach((name) => {
    const key = name.toLowerCase().trim();
    let id = byName.get(key);
    if (!id) {
      const partial = [...byName.keys()].find((candidate) => candidate.startsWith(key) || key.startsWith(candidate));
      if (partial) id = byName.get(partial);
    }
    if (id) anchorIds.push(id);
    else unmatchedAnchors.push(name);
  });
  return { anchorIds, unmatchedAnchors };
}

export function criteriaChips(criteria, industries) {
  if (!criteria) return [];
  const chips = [];
  if (criteria.level) chips.push({ key: "level", label: `${criteria.level} level` });
  if (criteria.naics) {
    const industry = industries.find((item) => item.naics === criteria.naics);
    chips.push({
      key: "naics",
      label: industry ? `${industry.naics} ${industry.label.split(/[;,]/)[0]}` : criteria.naics,
    });
  }
  (criteria.anchorNames || []).forEach((name) => chips.push({ key: "anchor", label: `anchor: ${name}` }));
  if (criteria.stateNames?.length) {
    chips.push({
      key: "states",
      label: criteria.stateNames.length > 3 ? `${criteria.stateNames.length} states` : criteria.stateNames.join(", "),
    });
  }
  if (criteria.emphasis) chips.push({ key: "emphasis", label: criteria.emphasis });
  if (criteria.minLq) chips.push({ key: "minLq", label: `LQ >= ${criteria.minLq}` });
  if (criteria.maxPopulation) chips.push({ key: "maxPop", label: `pop <= ${formatNumber.format(criteria.maxPopulation)}` });
  if (criteria.minPopulation) chips.push({ key: "minPop", label: `pop >= ${formatNumber.format(criteria.minPopulation)}` });
  return chips;
}

function rowStates(row, mode) {
  if (mode === "state") return [String(row.name || "").toLowerCase()];
  if (mode === "metro") return String(row.states || "").split(",").map((state) => state.trim().toLowerCase());
  return [String(row.state || "").toLowerCase()];
}

function passesCriteria(row, industry, mode, criteria) {
  if (!criteria) return true;
  if (criteria.stateNames?.length) {
    const stateSet = new Set(criteria.stateNames.map((state) => state.toLowerCase()));
    if (!rowStates(row, mode).some((state) => stateSet.has(state))) return false;
  }
  if (criteria.minLq != null && (industry.lq || 0) < criteria.minLq) return false;
  if (criteria.minPopulation != null && (row.population || 0) < criteria.minPopulation) return false;
  if (criteria.maxPopulation != null && (row.population || 0) > criteria.maxPopulation) return false;
  return true;
}

export function calculateRecommendations({ data, indexes, mode, selectedNaics, anchors, layers, criteria }) {
  const config = getModeConfig(mode);
  const geoRows = data[config.geoKey] || [];
  const industryRows = data[config.industryKey] || [];
  const geoMap = indexes[config.indexKey];
  const maxRank = config.maxRank;
  const selectedIndustry = (data.all_industries || data.industries || [])
    .find((industry) => industry.naics === selectedNaics);
  if (!selectedIndustry) return [];
  const selectedIndustryRows = industryRows.filter((row) => row.naics === selectedNaics);
  const industryMap = new Map(
    selectedIndustryRows.map((row) => [
      row[config.idField],
      { ...row, label: selectedIndustry.label },
    ]),
  );
  const maxEmployment = Math.max(...selectedIndustryRows.map((row) => row.emp), 1);
  const selectedAnchors = [...anchors].map((id) => geoMap.get(id)).filter(Boolean);
  const referenceRank = selectedAnchors.length
    ? Math.min(...selectedAnchors.map((row) => row.rank))
    : null;
  const weights = resolveWeights(criteria);

  let rows = geoRows
    .map((geo) => {
      const id = getGeoId(mode, geo);
      const industry = industryMap.get(id);
      if (!industry) return null;
      if (!passesCriteria(geo, industry, mode, criteria)) return null;

      const rankGap = referenceRank ? geo.rank - referenceRank : 0;
      const anchorCompatible = !referenceRank || rankGap > 0;
      const lqComponent = clamp((industry.lq || 0) / 3, 0, 1);
      const scaleComponent = logScale(industry.emp, maxEmployment);
      const rankGapComponent = referenceRank
        ? clamp(rankGap / (maxRank * 0.35), 0, 1)
        : 1 - geo.rank / maxRank;
      const competitiveness = 1 - geo.rank / maxRank;
      const growth = clamp((geo.avg_growth_pct || geo.investment_signal || 50) / 100, 0, 1);
      const score =
        100 *
        (weights.lq * lqComponent +
          weights.scale * scaleComponent +
          weights.gap * rankGapComponent +
          weights.momentum * competitiveness +
          weights.growth * growth);

      return {
        ...geo,
        key: id,
        industry,
        rankGap,
        anchorCompatible,
        recScore: Math.round(score * 10) / 10,
        reason: buildReason(geo, industry, rankGap, referenceRank),
      };
    })
    .filter(Boolean);

  if (layers.anchorOnly && referenceRank) rows = rows.filter((row) => row.anchorCompatible);
  if (referenceRank) rows = rows.filter((row) => row.anchorCompatible || anchors.has(row.key));
  rows = rows.filter((row) => !anchors.has(row.key));
  return rows.sort((a, b) => b.recScore - a.recScore).slice(0, 50);
}

export function toRecommendationCsv(rows, mode, selectedNaics) {
  const headers = [
    "rank",
    "geography",
    "state",
    "pe_rank",
    "pe_score",
    "recommendation_score",
    "naics",
    "industry",
    "lq",
    "industry_emp",
    "establishments",
    "rank_gap",
    "reason",
  ];
  const body = rows.slice(0, 100).map((row, index) => {
    const values = [
      index + 1,
      mode === "metro" ? row.name : labelGeo(row),
      row.state || row.name,
      row.rank,
      row.score,
      row.recScore,
      row.industry.naics,
      row.industry.label,
      row.industry.lq,
      row.industry.emp,
      row.industry.estab,
      row.rankGap,
      row.reason,
    ];
    return values.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(",");
  });
  return {
    filename: `geographic-targeting-${mode}-${selectedNaics}.csv`,
    content: [headers.join(","), ...body].join("\n"),
  };
}
